import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Collapse, Table, Alert, Spinner, InputGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ChevronDown, ChevronUp, Banknote, Trash2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import sv from 'date-fns/locale/sv';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { ASSET_FORM_STYLES, formatNumber, round2, unwrapData, k1ReasonMessage } from './assetUtils';

/**
 * DisposalModal — plain-question disposal flow (Sälj / Skrota) with a
 * mandatory backend preview before anything posts:
 *   type + proceeds → POST /assets/:id/dispose/preview → composite preview
 *   (bokfört värde, vinst/förlust + konto, momsrad eller personbils-
 *   förklaring, verifikatrader) → Bekräfta → POST /assets/:id/dispose.
 */
const DisposalModal = ({ show, onHide, companyId, asset, onDone, initialType = 'sale' }) => {
  const [type, setType] = useState(initialType);
  const [proceeds, setProceeds] = useState('');
  const [date, setDate] = useState(new Date());
  const [units, setUnits] = useState('1');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showRows, setShowRows] = useState(false);
  const [error, setError] = useState(null);

  // Delavyttring: en gemensam post (grouped) med antal > 1 kan avvecklas
  // delvis — resterande enheter fortsätter som en egen post (backend delar).
  const quantity = Math.max(parseInt(asset?.quantity, 10) || 1, 1);
  const isGrouped = quantity > 1;
  // K1 (BAS 2018 K1): pool assets book proceeds against Årets ersättning —
  // no gain/loss at disposal (BFNAR 2006:1 p. 6.37, 7.4/7.9) — and
  // insurance compensation is its own event type (IL 18 kap. 15 §).
  const k1Model = String(asset?.accounting_model || '').startsWith('k1');
  const isK1Pool = asset?.accounting_model === 'k1_collective_pool';

  useEffect(() => {
    if (show) {
      setType(initialType);
      setProceeds('');
      setDate(new Date());
      setUnits(String(Math.max(parseInt(asset?.quantity, 10) || 1, 1)));
      setPreview(null);
      setShowRows(false);
      setError(null);
    }
  }, [show, initialType, asset]);

  // Any input change invalidates a fetched preview.
  const invalidate = () => {
    setPreview(null);
    setError(null);
  };

  const proceedsNumber = round2(proceeds);
  const unitsNumber = parseInt(units, 10) || 0;
  const unitsValid = !isGrouped || (unitsNumber >= 1 && unitsNumber <= quantity);
  const canPreview = unitsValid && (type === 'scrap' || (proceeds !== '' && proceedsNumber >= 0));
  const takesProceeds = type === 'sale' || type === 'insurance';

  const fetchPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/assets/${asset.asset_id}/dispose/preview`),
        {
          type,
          date: date.toISOString().split('T')[0],
          ...(takesProceeds && { proceeds: proceedsNumber }),
          ...(isGrouped && { units: unitsNumber }),
        },
        { withCredentials: true }
      );
      setPreview(unwrapData(response));
    } catch (err) {
      setError(k1Model ? k1ReasonMessage(err) : (err.response?.data?.error || 'Kunde inte förhandsgranska avvecklingen. Försök igen.'));
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirmDisposal = async () => {
    setConfirming(true);
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/${asset.asset_id}/dispose`),
        {
          type,
          date: date.toISOString().split('T')[0],
          ...(takesProceeds && { proceeds: proceedsNumber }),
          ...(isGrouped && { units: unitsNumber }),
        },
        { withCredentials: true }
      );
      toast.success(type === 'sale' ? 'Försäljningen är bokförd.'
        : type === 'insurance' ? 'Försäkringsersättningen är bokförd.'
        : isK1Pool ? 'Tillgången är utrangerad och struken ur förteckningen.'
        : 'Utrangeringen är bokförd.');
      if (onDone) onDone();
      onHide();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte bokföra avvecklingen.');
    } finally {
      setConfirming(false);
    }
  };

  const renderGainLoss = () => {
    const gainLoss = parseFloat(preview?.gain_loss ?? preview?.result ?? 0) || 0;
    const account = preview?.gain_loss_account || preview?.result_account;
    if (gainLoss > 0) {
      return (
        <span>
          Försäljningen ger en <strong>vinst på {formatNumber(gainLoss)} kr</strong>
          {account ? <> som bokförs på konto {account}</> : null}.
        </span>
      );
    }
    if (gainLoss < 0) {
      return (
        <span>
          Avvecklingen ger en <strong>förlust på {formatNumber(Math.abs(gainLoss))} kr</strong>
          {account ? <> som bokförs på konto {account}</> : null}.
        </span>
      );
    }
    return <span>Ingen vinst eller förlust uppstår.</span>;
  };

  // Backend contract (AssetDisposalService.previewDisposal): rows carry
  // {account, type: 'debit'|'credit', amount}; book value sits on
  // preview.asset.book_value; VAT fields are vat / vatExempt / vatExemptReason.
  const rows = (preview?.rows || preview?.verification_rows || []).map((row) => ({
    account_number: row.account ?? row.account_number,
    account_name: row.account_name,
    debit: row.debit ?? (row.type === 'debit' ? row.amount : 0),
    credit: row.credit ?? (row.type === 'credit' ? row.amount : 0),
  }));
  const vatAmount = parseFloat(preview?.vat ?? preview?.vat_amount) || 0;
  const vatExempt = Boolean(preview?.vatExempt ?? preview?.vat_exempt);
  const previewBookValue = preview?.asset?.book_value ?? preview?.book_value ?? 0;
  const vatExplanation = preview?.vatExemptReason || preview?.vat_explanation;

  return (
    <Modal show={show} onHide={confirming ? undefined : onHide} centered contentClassName="asset-form">
      <Modal.Header closeButton={!confirming}>
        <Modal.Title as="h5">Avveckla {asset?.name || 'tillgång'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Sålde du den eller skrotades den?</Form.Label>
          <div className="d-flex gap-2">
            <Button
              variant={type === 'sale' ? 'primary' : 'outline-secondary'}
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={() => { setType('sale'); invalidate(); }}
            >
              <Banknote size={14} /> Sålt
            </Button>
            {isK1Pool && (
              <Button
                variant={type === 'insurance' ? 'primary' : 'outline-secondary'}
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={() => { setType('insurance'); invalidate(); }}
              >
                <ShieldCheck size={14} /> Försäkringsersättning
              </Button>
            )}
            <Button
              variant={type === 'scrap' ? 'primary' : 'outline-secondary'}
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={() => { setType('scrap'); invalidate(); }}
            >
              <Trash2 size={14} /> Skrotad
            </Button>
          </div>
        </Form.Group>

        {isGrouped && (
          <Form.Group className="mb-3" controlId="disposal-units">
            <Form.Label>Hur många enheter?</Form.Label>
            <Form.Control
              type="number"
              min="1"
              max={quantity}
              step="1"
              value={units}
              onChange={(e) => { setUnits(e.target.value); invalidate(); }}
              style={{ maxWidth: 140 }}
            />
            {!unitsValid ? (
              <Form.Text style={{ color: '#b91c1c' }}>
                Ange mellan 1 och {quantity} enheter.
              </Form.Text>
            ) : unitsNumber < quantity ? (
              <Form.Text muted>
                Resterande {quantity - unitsNumber} enheter fortsätter skrivas av som en egen post.
              </Form.Text>
            ) : (
              <Form.Text muted>Hela gruppen ({quantity} enheter) avvecklas.</Form.Text>
            )}
          </Form.Group>
        )}

        {takesProceeds && (
          <Form.Group className="mb-3" controlId="disposal-proceeds">
            <Form.Label>{type === 'insurance' ? 'Ersättning från försäkringsbolaget (kr)' : 'För hur mycket? (exkl. moms)'}</Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={proceeds}
                onChange={(e) => { setProceeds(e.target.value); invalidate(); }}
                placeholder="0"
              />
              <InputGroup.Text>kr</InputGroup.Text>
            </InputGroup>
          </Form.Group>
        )}

        <Form.Group className="mb-3" controlId="disposal-date">
          <Form.Label>När?</Form.Label>
          <div>
            <DatePicker
              selected={date}
              onChange={(value) => { setDate(value); invalidate(); }}
              dateFormat="yyyy-MM-dd"
              locale={sv}
              className="form-control"
            />
          </div>
        </Form.Group>

        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        {!preview && (
          <Button variant="outline-primary" disabled={!canPreview || loadingPreview} onClick={fetchPreview}>
            {loadingPreview ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Räknar…
              </>
            ) : (
              'Visa vad som bokförs'
            )}
          </Button>
        )}

        {preview && (
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: '1rem 1.25rem',
              fontSize: '0.875rem',
              lineHeight: 1.6,
            }}
          >
            {!isK1Pool && (
              <div>
                Bokfört värde vid avveckling:{' '}
                <strong>{formatNumber(previewBookValue)} kr</strong>
              </div>
            )}
            <div className="mt-1">
              {isK1Pool
                ? <span>{preview.explanation || preview.resultText || 'Ersättningen minskar det kollektiva bokförda värdet.'}</span>
                : renderGainLoss()}
            </div>
            <div className="mt-1">
              {type === 'sale' ? (
                vatExempt ? (
                  <span style={{ color: '#92400e' }}>
                    {vatExplanation
                      || 'Ingen moms läggs på försäljningen — momsen var inte avdragsgill vid köpet, därför är försäljningen momsfri.'}
                  </span>
                ) : vatAmount > 0 ? (
                  <span>Utgående moms: <strong>{formatNumber(vatAmount)} kr</strong></span>
                ) : null
              ) : null}
            </div>

            {rows.length > 0 && (
              <div className="mt-2">
                <Button
                  variant="link"
                  size="sm"
                  style={{ textDecoration: 'none', color: 'var(--primary)', paddingLeft: 0 }}
                  onClick={() => setShowRows((prev) => !prev)}
                >
                  {showRows ? 'Dölj verifikatrader' : 'Visa verifikatrader'}{' '}
                  {showRows ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </Button>
                <Collapse in={showRows}>
                  <div>
                    <Table size="sm" className="mb-0 mt-1 asset-table">
                      <thead>
                        <tr>
                          <th>Konto</th>
                          <th className="text-end">Debet</th>
                          <th className="text-end">Kredit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={index}>
                            <td>
                              {row.account_number}
                              {row.account_name ? ` – ${row.account_name}` : ''}
                            </td>
                            <td className="text-end asset-num">
                              {parseFloat(row.debit) > 0 ? `${formatNumber(row.debit)} kr` : ''}
                            </td>
                            <td className="text-end asset-num">
                              {parseFloat(row.credit) > 0 ? `${formatNumber(row.credit)} kr` : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Collapse>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={confirming}>
          Avbryt
        </Button>
        <Button variant="primary" onClick={confirmDisposal} disabled={!preview || confirming}>
          {confirming ? 'Bokför…' : 'Bekräfta'}
        </Button>
      </Modal.Footer>
      <style>{ASSET_FORM_STYLES}</style>
    </Modal>
  );
};

export default DisposalModal;
